'use client'

import { useState, useMemo } from 'react'

import { PepType } from '@marketplace-web/pay-identity/politically-exposed-person-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  getPaymentsAccount,
  transformDynamicConfiguration,
  transformPaymentsAccount,
  transformRequirements,
  PaymentsAccountModel,
  DynamicConfigurationModel,
  RequirementsModel,
} from '@marketplace-web/pay-identity/payments-account-data'
import {
  AddressModel,
  AddressType,
  getDefaultBillingAddress,
  transformAddress,
} from '@marketplace-web/address/address-data'

import { ActivateBalanceFormModel } from './types'

export const useBalanceActivationState = () => {
  const [form, setForm] = useState<ActivateBalanceFormModel>({
    firstName: '',
    lastName: '',
    birthdate: '',
    ssn: '',
    personalIdNumber: '',
    nationality: null,
    pepType: undefined,
    pepPosition: '',
    pepOrganisation: '',
    pepPositionDate: '',
  })

  const [address, setAddress] = useState<AddressModel | null>(null)
  const [status, setStatus] = useState<UiState>(UiState.Idle)
  const [isBalanceActivated, setIsBalanceActivated] = useState(false)
  const [dynamicConfiguration, setDynamicConfiguration] =
    useState<DynamicConfigurationModel | null>(null)
  const [requirements, setRequirements] = useState<RequirementsModel | null>(null)

  const firstName = useMemo(() => form.firstName, [form])
  const lastName = useMemo(() => form.lastName, [form])

  const isSsnNumberRequired = useMemo(() => requirements?.ssnSerial || false, [requirements])

  const isNationalityRequired = useMemo(() => requirements?.nationality || false, [requirements])

  const agreementHint = useMemo(
    () => dynamicConfiguration?.agreementHint || '',
    [dynamicConfiguration],
  )

  const isPepDeclarationRequired = useMemo(
    () => requirements?.pepDeclaration || false,
    [requirements],
  )

  const personalIdNumberRequiredNationalities = useMemo(
    () => dynamicConfiguration?.personalIdNumberRequiredNationalities || [],
    [dynamicConfiguration],
  )

  const addressType = useMemo(
    () => dynamicConfiguration?.addressType || AddressType.Billing,
    [dynamicConfiguration],
  )

  const lockedFields = useMemo(
    () => dynamicConfiguration?.lockedFields || [],
    [dynamicConfiguration],
  )

  const maskedFields = useMemo(
    () => dynamicConfiguration?.maskedFields || [],
    [dynamicConfiguration],
  )

  const showInformationHint = useMemo(
    () => dynamicConfiguration?.showInformationHint || false,
    [dynamicConfiguration],
  )

  const bannedNationalities = useMemo(
    () => dynamicConfiguration?.bannedNationalities || [],
    [dynamicConfiguration],
  )

  const getDefaultFormValues = async (currentUserId: number | null) => {
    if (!currentUserId) return

    setStatus(UiState.Pending)

    let billingResponse
    let paymentsResponse

    await Promise.all([
      (billingResponse = await getDefaultBillingAddress()),
      (paymentsResponse = await getPaymentsAccount(currentUserId)),
    ])

    let addressNew: AddressModel | null = null
    let paymentsAccount: PaymentsAccountModel | null = null
    let dynamicConfigurationNew: DynamicConfigurationModel | null = null
    let requirementsNew: RequirementsModel | null = null

    if ('user_address' in billingResponse) {
      addressNew = transformAddress(billingResponse.user_address)
    }

    if ('payments_account' in paymentsResponse && paymentsResponse.payments_account) {
      paymentsAccount = transformPaymentsAccount(paymentsResponse.payments_account)
    }

    if ('dynamic_configuration' in paymentsResponse && paymentsResponse.dynamic_configuration) {
      dynamicConfigurationNew = transformDynamicConfiguration(
        paymentsResponse.dynamic_configuration,
      )
    }

    if ('requirements' in paymentsResponse && paymentsResponse.requirements) {
      requirementsNew = transformRequirements(paymentsResponse.requirements)
    }

    const pepDetails = paymentsAccount?.pepDetails

    const formNew: ActivateBalanceFormModel = {
      firstName: paymentsAccount?.firstName || '',
      lastName: paymentsAccount?.lastName || '',
      birthdate: paymentsAccount?.birthdate || '',
      ssn: '',
      personalIdNumber: '',
      nationality: paymentsAccount?.nationality || null,
      pepType: pepDetails ? pepDetails.type || PepType.NotPep : undefined,
      pepFullName: pepDetails?.associatedPepFullName || '',
      pepOrganisation: pepDetails?.organisation || '',
      pepPosition: pepDetails?.position || '',
      pepPositionDate: pepDetails?.holdsPositionFrom || '',
    }

    setForm(formNew)
    setIsBalanceActivated(!!paymentsAccount?.balanceActivated)
    setDynamicConfiguration(dynamicConfigurationNew)
    setRequirements(requirementsNew)
    setAddress(addressNew)
    setStatus(UiState.Success)
  }

  return {
    form,
    setForm,
    address,
    setAddress,
    status,
    setStatus,
    isBalanceActivated,
    setIsBalanceActivated,
    agreementHint,
    dynamicConfiguration,
    setDynamicConfiguration,
    requirements,
    setRequirements,
    getDefaultFormValues,
    firstName,
    lastName,
    addressType,
    lockedFields,
    maskedFields,
    bannedNationalities,
    showInformationHint,
    isSsnNumberRequired,
    isNationalityRequired,
    isPepDeclarationRequired,
    personalIdNumberRequiredNationalities,
  }
}
